import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { api } from '../api/client.js';
import { isReservedStoreSlug } from '../config/reservedSlugs.js';
import { useTheme } from '../theme/ThemeContext.jsx';
import { parseStoreAccent, storeAccentCssVariables } from '../theme/storeAccent.js';
import { StorePwaBootstrap } from '../components/store/StorePwaBootstrap.jsx';

function SunIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  );
}

function MoonIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden {...props}>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

/** Pedido de e-mail para redefinir senha do cliente na vitrine (`/:slug/recuperar-senha`). */
export function StoreRecoverPasswordPage() {
  const { slug } = useParams();
  const { scheme, toggle } = useTheme();
  const [tenant, setTenant] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState('');
  const [doneMsg, setDoneMsg] = useState('');

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    api(`/store/${encodeURIComponent(slug)}/catalog`, { skipAuth: true })
      .then((payload) => {
        if (cancelled) return;
        const t = payload?.tenant;
        setTenant(t && typeof t === 'object' ? t : null);
        setLoadErr('');
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadErr(e.message || 'Não foi possível carregar a loja.');
        setTenant(null);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const accentStyle = useMemo(
    () => storeAccentCssVariables(parseStoreAccent(tenant?.theme_json)),
    [tenant?.theme_json],
  );

  const nomeLoja = tenant?.nome ? String(tenant.nome) : slug || 'Loja';
  const lojaPath = slug ? `/${encodeURIComponent(slug)}` : '/';

  if (!slug || isReservedStoreSlug(slug)) {
    return <Navigate to="/" replace />;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setFormErr('');
    setDoneMsg('');
    const em = email.trim().toLowerCase();
    if (!em) {
      setFormErr('Indique o e-mail da sua conta.');
      return;
    }
    setBusy(true);
    try {
      const res = await api(`/store/${encodeURIComponent(slug)}/customer/password-reset-request`, {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify({ email: em }),
      });
      setDoneMsg(String(res?.message || 'Se existir conta, enviámos um e-mail com o link.'));
      setEmail('');
    } catch (err) {
      setFormErr(err.message || 'Não foi possível enviar o pedido.');
    } finally {
      setBusy(false);
    }
  }

  const shellCls =
    'min-h-dvh bg-zinc-50 text-zinc-900 transition-colors dark:bg-[#06090c] dark:text-zinc-100';

  return (
    <div className={shellCls} style={accentStyle}>
      <StorePwaBootstrap slug={slug ? String(slug).toLowerCase() : ''} tenant={tenant} />
      <header className="sticky top-0 z-10 border-b border-zinc-200/80 bg-white/80 backdrop-blur dark:border-zinc-800/80 dark:bg-zinc-900/75">
        <div className="mx-auto flex max-w-lg items-center justify-between gap-3 px-4 py-3">
          <Link to={lojaPath} className="text-sm font-semibold text-[var(--st-700)] hover:underline dark:text-[var(--st-400)]">
            ← Voltar à loja
          </Link>
          <button
            type="button"
            onClick={toggle}
            aria-label={scheme === 'dark' ? 'Modo claro' : 'Modo escuro'}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-zinc-200 text-zinc-600 dark:border-zinc-600 dark:text-zinc-300"
          >
            {scheme === 'dark' ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-10">
        <p className="text-center text-[10px] font-bold uppercase tracking-[0.35em] text-zinc-400 dark:text-zinc-500">
          Recuperação de conta
        </p>
        <h1 className="mt-2 text-center font-display text-2xl font-bold text-zinc-900 dark:text-zinc-50">
          Esqueceu a senha?
        </h1>
        <p className="mt-2 text-center text-sm text-zinc-600 dark:text-zinc-400">
          Loja <span className="font-semibold text-[var(--st-700)] dark:text-[var(--st-400)]">{nomeLoja}</span>
        </p>

        {loadErr ? (
          <p className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
            {loadErr}
          </p>
        ) : null}

        {!loadErr && (
          <form
            onSubmit={onSubmit}
            className="mt-8 space-y-4 rounded-3xl border border-zinc-200/90 bg-white p-6 shadow-xl dark:border-zinc-700/80 dark:bg-zinc-900/90"
          >
            <p className="text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
              Indique o e-mail com que se cadastrou nesta loja. Se existir uma conta, receberá um e-mail com um link
              seguro (válido cerca de 1 hora) para criar uma nova senha.
            </p>
            <label className="block text-sm font-semibold">
              E-mail
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm dark:border-zinc-600 dark:bg-zinc-950"
                placeholder="seu@email.com"
                disabled={busy}
              />
            </label>
            {formErr ? (
              <p className="text-sm font-medium text-red-600 dark:text-red-400">{formErr}</p>
            ) : null}
            {doneMsg ? (
              <p className="rounded-xl bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200">
                {doneMsg}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-2xl bg-gradient-to-br from-[var(--st-500)] to-[var(--st-600)] py-3 text-sm font-bold text-white shadow-lg transition hover:brightness-105 disabled:opacity-50"
            >
              {busy ? 'A enviar…' : 'Enviar link por e-mail'}
            </button>
          </form>
        )}

        <p className="mt-8 text-center text-xs text-zinc-500 dark:text-zinc-500">
          O e-mail é enviado pelo sistema OminIA quando o SMTP está configurado no servidor. Não recebeu? Verifique o
          spam ou contacte a loja.
        </p>
      </main>
    </div>
  );
}
